import React, { useMemo } from "react";
import { FiGlobe, FiLink, FiSearch, FiShare2 } from "react-icons/fi";
import type { AnalyticsBreakdownItem } from "../../hooks/useAnalytics";
import { aggregateSources, classifyReferrer, SOURCE_META, type SourceKind } from "./pagesInsightsModel";

/**
 * "Sources de trafic" : where sessions come from, from Cloudflare's referrer host. Four fixed
 * families (direct, search engines, social networks, other sites) as a 100 % stacked bar + legend
 * with shares, then the busiest referrer hosts, each tagged with its family. Internal navigation
 * (the site's own hostnames) is removed server-side, so a share here is a share of ARRIVALS.
 */

const compact = new Intl.NumberFormat("fr-FR", { notation: "compact", maximumFractionDigits: 1 });
const full = new Intl.NumberFormat("fr-FR");
const pct = new Intl.NumberFormat("fr-FR", { style: "percent", maximumFractionDigits: 1 });

const ICONS: Record<SourceKind, React.ReactNode> = {
	direct: <FiLink className="h-3.5 w-3.5" aria-hidden />,
	search: <FiSearch className="h-3.5 w-3.5" aria-hidden />,
	social: <FiShare2 className="h-3.5 w-3.5" aria-hidden />,
	other: <FiGlobe className="h-3.5 w-3.5" aria-hidden />,
};

export const TrafficSources: React.FC<{ referrers: AnalyticsBreakdownItem[]; loading?: boolean; maxHosts?: number }> = ({ referrers, loading, maxHosts = 7 }) => {
	const sources = useMemo(() => aggregateSources(referrers), [referrers]);
	const total = sources.reduce((s, g) => s + g.pageViews, 0);
	const hosts = useMemo(() => referrers.filter((r) => !!r.label).sort((a, b) => b.pageViews - a.pageViews).slice(0, maxHosts), [referrers, maxHosts]);
	const hostMax = hosts[0]?.pageViews ?? 0;

	return (
		<div className="min-w-0 rounded-xl border border-slate-200 bg-white shadow-sm">
			<div className="px-5 pt-4">
				<p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Sources de trafic</p>
				<p className="mt-0.5 text-xs text-slate-500">{loading ? "Chargement…" : total > 0 ? `${full.format(total)} arrivées sur la période` : "D'où viennent les visites"}</p>
			</div>

			<div className="px-5 pb-4 pt-3">
				{loading ? (
					<div className="h-24 animate-pulse rounded-lg bg-slate-100" aria-hidden />
				) : total === 0 ? (
					<p className="py-8 text-center text-xs text-slate-400">Aucune donnée de provenance pour cette période.</p>
				) : (
					<>
						{/* 100 % stacked bar, fixed family order, 2px surface gaps. */}
						<div className="flex h-3 w-full gap-0.5 overflow-hidden rounded-full" role="img" aria-label={sources.map((s) => `${s.label} ${pct.format(s.share)}`).join(", ")}>
							{sources
								.filter((s) => s.share > 0)
								.map((s) => (
									<div key={s.kind} className="h-full" style={{ width: `${Math.max(1, s.share * 100)}%`, background: s.color }} title={`${s.label} · ${pct.format(s.share)}`} />
								))}
						</div>
						<ul className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2">
							{sources.map((s) => (
								<li key={s.kind} className="flex items-center gap-2 text-xs" title={SOURCE_META[s.kind].hint}>
									<span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-white" style={{ background: s.color }}>
										{ICONS[s.kind]}
									</span>
									<span className="min-w-0 flex-1">
										<span className="block truncate font-semibold text-slate-700">{s.label}</span>
										<span className="block tabular-nums text-slate-400">
											{compact.format(s.pageViews)} · {pct.format(s.share)}
										</span>
									</span>
								</li>
							))}
						</ul>

						{hosts.length > 0 && (
							<>
								<p className="mt-4 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Principaux référents</p>
								<ul className="mt-1.5 space-y-2">
									{hosts.map((h) => {
										const kind = classifyReferrer(h.label);
										const share = total > 0 ? h.pageViews / total : 0;
										return (
											<li key={h.label}>
												<div className="flex items-center gap-2 text-[13px]">
													<span className="h-2 w-2 shrink-0 rounded-full" style={{ background: SOURCE_META[kind].color }} aria-hidden />
													<span className="min-w-0 flex-1 truncate font-medium text-slate-700" title={`${h.label} · ${SOURCE_META[kind].label}`}>
														{h.label}
													</span>
													<span className="shrink-0 text-xs tabular-nums text-slate-400">{pct.format(share)}</span>
													<span className="w-12 shrink-0 text-right font-semibold tabular-nums text-slate-600" title={full.format(h.pageViews)}>
														{compact.format(h.pageViews)}
													</span>
												</div>
												<div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100" aria-hidden>
													<div className="h-full rounded-full" style={{ width: `${hostMax > 0 ? Math.max(2, (h.pageViews / hostMax) * 100) : 0}%`, background: SOURCE_META[kind].color }} />
												</div>
											</li>
										);
									})}
								</ul>
							</>
						)}
					</>
				)}
			</div>
		</div>
	);
};

export default TrafficSources;
